import axios from '@/config/axios';
import notificationWs from '@/services/notificationWebSocket';

export default {
  name: 'NotificationBell',
  data() {
    return {
      isOpen: false,
      isLoading: false,
      notifications: [],
      unreadCount: 0,
      currentPage: 0,
      totalPages: 0,
      isLoadingMore: false,
      unsubscribeWs: null,
      countUnreadTimer: null,
    };
  },
  mounted() {
    // Lấy số đếm chưa đọc lần đầu và đăng ký lắng nghe WebSocket realtime
    this.fetchCountUnread();
    this.unsubscribeWs = notificationWs.subscribe(this.handleWsMessage);

    // Đóng dropdown khi click bên ngoài
    document.addEventListener('mousedown', this.handleOutsideClick);
  },
  beforeUnmount() {
    if (this.countUnreadTimer) {
      clearTimeout(this.countUnreadTimer);
      this.countUnreadTimer = null;
    }
    if (this.unsubscribeWs) {
      this.unsubscribeWs();
      this.unsubscribeWs = null;
    }
    document.removeEventListener('mousedown', this.handleOutsideClick);
  },
  methods: {
    /** Kiểm tra một thông báo có phải là chưa đọc hay không */
    isNotiUnread(noti) {
      if (!noti) return false;
      return noti.read !== true && noti.isRead !== true;
    },

    /** Parse an ID or notification DTO safely from payload content */
    parsePayloadNotiId(content) {
      if (content === null || content === undefined) return null;
      if (typeof content === 'number') return content;
      if (typeof content === 'object' && content.id) return Number(content.id);
      if (typeof content === 'string') {
        const trimmed = content.trim();
        if (/^\d+$/.test(trimmed)) {
          return Number(trimmed);
        }
        try {
          const parsed = JSON.parse(trimmed);
          if (parsed && typeof parsed === 'object' && parsed.id) {
            return Number(parsed.id);
          }
          if (typeof parsed === 'number') {
            return parsed;
          }
        } catch (e) {
          // ignore json parse error
        }
      }
      return null;
    },

    /** Xử lý tin nhắn WebSocket nhận được từ Singleton service */
    handleWsMessage(payload) {
      if (!payload) return;

      // Khi WebSocket vừa kết nối/reconnect thành công
      if (payload.type === 'WS_CONNECTED') {
        this.fetchCountUnread();
        return;
      }

      // Xử lý thông báo mới: NOTIFY
      if (payload.type === 'NOTIFY') {
        let dto = null;
        let notiId = null;

        if (typeof payload.content === 'string') {
          try {
            const parsed = JSON.parse(payload.content);
            if (parsed && typeof parsed === 'object' && parsed.id) {
              dto = parsed;
              notiId = Number(parsed.id);
            }
          } catch (e) {
            // Chuỗi ID đơn thuần
            if (/^\d+$/.test(payload.content.trim())) {
              notiId = Number(payload.content.trim());
            }
          }
        } else if (payload.content && typeof payload.content === 'object' && payload.content.id) {
          dto = payload.content;
          notiId = Number(payload.content.id);
        }

        if (!notiId && !dto) return;

        // Tránh tăng trùng lặp nếu ID này đã có trong danh sách
        const exists = notiId && this.notifications.some(n => Number(n.id) === Number(notiId));
        if (!exists) {
          this.unreadCount = (this.unreadCount || 0) + 1;
        }

        if (this.isOpen) {
          if (dto && !exists) {
            // Chèn ngay vào đầu danh sách với độ trễ 0ms, không làm giật UI
            this.notifications.unshift(dto);
          } else if (!dto) {
            // Fallback tải lại ngầm nếu payload chỉ có id
            this.fetchNotificationsSilently(0);
          }
        }
        return;
      }

      // Xử lý hủy thông báo: UN_NOTIFY (khi hủy like, dislike, xóa comment, xóa reply)
      if (payload.type === 'UN_NOTIFY') {
        const notiId = this.parsePayloadNotiId(payload.content);
        if (!notiId) return;

        const index = this.notifications.findIndex(n => Number(n.id) === Number(notiId));
        if (index !== -1) {
          const removedNoti = this.notifications[index];
          // Nếu thông báo bị xóa chưa đọc -> trừ badge
          if (this.isNotiUnread(removedNoti)) {
            this.unreadCount = Math.max(0, (this.unreadCount || 0) - 1);
          }
          // Xóa thông báo khỏi danh sách hiển thị ngay lập tức (dù đã xem hay chưa xem)
          this.notifications.splice(index, 1);
        } else {
          // Khi thông báo không có trong list đang mở (dropdown đóng) -> trừ ngay badge optimistically
          this.unreadCount = Math.max(0, (this.unreadCount || 0) - 1);
        }

        // Đồng bộ lại count unread ngầm từ server sau một khoảng nhỏ
        if (this.countUnreadTimer) {
          clearTimeout(this.countUnreadTimer);
        }
        this.countUnreadTimer = setTimeout(() => {
          this.fetchCountUnread();
        }, 300);
      }
    },

    /** Chỉ lấy số badge chưa đọc */
    async fetchCountUnread() {
      try {
        const res = await axios.get('notification/v1/count-unread');
        if (res.data?.status === '200') {
          this.unreadCount = res.data.result?.unreadCount ?? 0;
        }
      } catch (e) {
        // Không hiển thị lỗi cho badge poll
      }
    },

    /** Mở/đóng dropdown */
    async toggleDropdown() {
      if (this.isOpen) {
        this.isOpen = false;
        return;
      }

      this.isOpen = true;
      // Reset và load trang đầu
      this.notifications = [];
      this.currentPage = 0;
      await this.fetchNotifications(0);
    },

    /** Lấy danh sách thông báo */
    async fetchNotifications(page = 0) {
      if (page === 0) {
        this.isLoading = true;
      } else {
        this.isLoadingMore = true;
      }

      try {
        const res = await axios.get('notification/v1/list', { params: { page } });
        if (res.data?.status === '200') {
          const data = res.data.result;
          const notiPage = data?.notifications;

          if (page === 0) {
            this.notifications = notiPage?.content || [];
          } else {
            this.notifications = [...this.notifications, ...(notiPage?.content || [])];
          }

          this.unreadCount = data?.unreadCount ?? 0;
          this.currentPage = notiPage?.number ?? 0;
          this.totalPages = notiPage?.totalPages ?? 0;
        }
      } catch (e) {
        console.error('Error fetching notifications', e);
      } finally {
        this.isLoading = false;
        this.isLoadingMore = false;
      }
    },

    /** Tải lại thông báo trang 0 trong nền mà không hiện loading spinner */
    async fetchNotificationsSilently(page = 0) {
      try {
        const res = await axios.get('notification/v1/list', { params: { page } });
        if (res.data?.status === '200') {
          const data = res.data.result;
          const notiPage = data?.notifications;
          if (notiPage?.content) {
            this.notifications = notiPage.content;
          }
          this.unreadCount = data?.unreadCount ?? 0;
          this.currentPage = notiPage?.number ?? 0;
          this.totalPages = notiPage?.totalPages ?? 0;
        }
      } catch (e) {
        console.error('Error silently fetching notifications', e);
      }
    },

    /** Load thêm trang tiếp theo */
    async loadMore() {
      if (this.currentPage + 1 < this.totalPages) {
        await this.fetchNotifications(this.currentPage + 1);
      }
    },

    /** Đánh dấu tất cả đã đọc */
    async markAllAsRead() {
      if (this.unreadCount === 0) return;
      try {
        await axios.post('notification/mark-all-read');
        this.notifications = this.notifications.map(n => ({ ...n, read: true, isRead: true }));
        this.unreadCount = 0;
      } catch (e) {
        console.error('Error marking all as read', e);
      }
    },

    /** Click vào một thông báo */
    async onNotificationClick(noti) {
      // Đánh dấu đã đọc nếu chưa
      if (this.isNotiUnread(noti)) {
        try {
          await axios.post(`notification/mark-read/${noti.id}`);
          noti.read = true;
          noti.isRead = true;
          if (this.unreadCount > 0) this.unreadCount--;
        } catch (e) {
          // bỏ qua lỗi mark read
        }
      }

      // Navigate đến trang sách
      if (noti.bookId) {
        const query = {};
        if (noti.reviewId) query.reviewId = noti.reviewId;
        if (noti.refId) query.refId = noti.refId;
        if (noti.type) query.type = noti.type;

        this.$router.push({
          path: `/book/${noti.bookId}`,
          query: query
        }).catch(err => {
          if (err.name !== 'NavigationDuplicated') console.error(err);
        });
      }

      this.isOpen = false;
    },

    /** Đóng dropdown khi click bên ngoài */
    handleOutsideClick(e) {
      if (!this.isOpen) return;
      const wrapper = this.$el;
      if (wrapper && !wrapper.contains(e.target)) {
        this.isOpen = false;
      }
    },

    /** Định dạng thời gian */
    formatTime(createTime) {
      if (!createTime || createTime.length < 14) return '';
      // createTime format: yyyyMMddHHmmss
      const y = createTime.substring(0, 4);
      const mo = createTime.substring(4, 6);
      const d = createTime.substring(6, 8);
      const h = createTime.substring(8, 10);
      const mi = createTime.substring(10, 12);

      const date = new Date(`${y}-${mo}-${d}T${h}:${mi}:00`);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);

      if (diffMins < 1) return 'Vừa xong';
      if (diffMins < 60) return `${diffMins} phút trước`;
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours} giờ trước`;
      const diffDays = Math.floor(diffHours / 24);
      if (diffDays < 7) return `${diffDays} ngày trước`;
      return `${d}/${mo}/${y}`;
    },

    /** Text mô tả theo type */
    getNotificationText(noti) {
      if (noti.type === 'REVIEW_COMMENT') {
        return `đã bình luận vào nhận xét của bạn`;
      }
      if (noti.type === 'COMMENT_REPLY') {
        return `đã trả lời bình luận của bạn`;
      }
      if (noti.type === 'REVIEW_LIKE') {
        return `đã thích nhận xét của bạn`;
      }
      if (noti.type === 'REVIEW_DISLIKE') {
        return `không thích nhận xét của bạn`;
      }
      if (noti.type === 'COMMENT_LIKE') {
        return `đã thích bình luận của bạn`;
      }
      if (noti.type === 'COMMENT_DISLIKE') {
        return `không thích bình luận của bạn`;
      }
      return `đã tương tác với bạn`;
    },

    /** Lấy chữ cái đầu tên để hiển thị fallback avatar */
    getInitial(name) {
      if (!name) return '?';
      return name.charAt(0).toUpperCase();
    },

    /** Kiểm tra avatar có hợp lệ không */
    isValidAvatar(avatar) {
      return avatar && avatar !== 'NO_AVATAR' && avatar.trim() !== '';
    },

    /** Trả về src đúng cho avatar: URL thật dùng trực tiếp, còn lại thêm base64 prefix */
    getAvatarSrc(avatar) {
      if (!avatar || avatar === 'NO_AVATAR') return '';
      // Nếu đã là data URI hoặc http URL → dùng trực tiếp
      if (avatar.startsWith('data:') || avatar.startsWith('http://') || avatar.startsWith('https://')) {
        return avatar;
      }
      // Còn lại là raw base64
      return 'data:image/png;base64,' + avatar;
    },

    /** Khi ảnh lỗi → ẩn img để fallback avatar chữ cái hiển thị */
    onAvatarError(event) {
      if (event && event.target) {
        event.target.style.display = 'none';
      }
    },

    /** Unread count hiển thị (tối đa 99+) */
    badgeCount() {
      if (this.unreadCount > 99) return '99+';
      return this.unreadCount;
    },

    hasMore() {
      return this.currentPage + 1 < this.totalPages;
    }
  }
};
